import React from 'react';
import { styled } from '@components-shared/src/stitches';
import { UserInfo } from '@components/Header/NavMenu/UserInfo';
import { StyledScroll } from '@components/main-components/containers/RadixScroll';
import { Divider } from '@components/main-components/divider';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { Sheet } from '@components/main-components/sheet/Sheet';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { trackClickEvent } from '@lib/analytics';
import { useHeaderStore } from '@store/header';
import { EXTERNAL_PATHS, HELP_PATHS } from '@utils/constants';
import { ConnectWallet } from '../ConnectWallet';
import { NewNavLink, SubContainer } from '../styles';
import { FooterIcons } from './FooterIcons';
import { NavDivider, NavLinks } from './Links';
import { UserInfoSkeleton } from './MobileUserOptions';
import { WalletBalance } from './WalletBalance';

const RESOURCES_OPTIONS = [
  {
    title: 'About',
    href: '/about',
  },
  {
    title: 'FAQs',
    href: HELP_PATHS.Home,
  },
];

const Links = () => {
  const { close } = useHeaderStore();

  return (
    <Container>
      {RESOURCES_OPTIONS.map(option => (
        <AccessibleLink
          key={`${option.title}-${option.href}`}
          absoluteUri={option.href}
          onClick={close}
          type="external"
        >
          <NewNavLink size="L">{option.title}</NewNavLink>
        </AccessibleLink>
      ))}
      <AccessibleLink absoluteUri={EXTERNAL_PATHS.Market} onClick={close} type="external">
        <NewNavLink size="L">Market</NewNavLink>
      </AccessibleLink>
    </Container>
  );
};

const AuthenticatedNavContent = ({
  handleContentOnTouchStart,
  handleContentOnTouchEnd,
}: {
  handleContentOnTouchStart: () => void;
  handleContentOnTouchEnd: () => void;
}) => {
  const { authUser } = useAuth();
  const { logout } = useConnectedAuth();
  const { close } = useHeaderStore();

  const onLogout = () => {
    trackClickEvent({
      event: 'Logout Clicked',
      properties: {
        sourceComponent: 'Navigation',
      },
    });
    close();
    logout();
  };

  return (
    <div>
      <StickyContainer>
        <UserInfoContainer>
          {!authUser ? <UserInfoSkeleton /> : <UserInfo userInfo={authUser} newMobileNav />}
        </UserInfoContainer>
        <OptionsDivider />
      </StickyContainer>
      <NavList
        onTouchStart={handleContentOnTouchStart}
        onTouchEnd={handleContentOnTouchEnd}
        variant="authenticated"
      >
        <SubContainer variant="mobile">
          <WalletBalance variant="mobile" />
          <Container auth>
            <NavLinks variant="mobile" />
            <NavDivider />

            <NewNavLink onClick={onLogout}>Logout</NewNavLink>
          </Container>
        </SubContainer>
      </NavList>
    </div>
  );
};

const DefaultNavContent = () => (
  <NavList>
    <SubContainer variant="mobile">
      <Links />
    </SubContainer>
  </NavList>
);

export const NewMobileHamburgerSheet = () => {
  const { isHamburgerMenuOpen, close } = useHeaderStore();
  const { isAuthenticatedWithAPI, isConnectingAPIUser } = useAuth();

  const [isDraggingContent, setIsDraggingContent] = React.useState(false);
  const handleContentOnTouchStart = React.useCallback(() => {
    setIsDraggingContent(true);
  }, []);

  const handleContentOnTouchEnd = React.useCallback(() => {
    setIsDraggingContent(false);
  }, []);
  return (
    <Sheet
      isOpen={isHamburgerMenuOpen}
      onClose={close}
      detent="content-height"
      disableDrag={isDraggingContent}
    >
      <SheetContainer hasThumb>
        <NavContent auth={isAuthenticatedWithAPI}>
          {!isAuthenticatedWithAPI && <DefaultNavContent />}
          {isAuthenticatedWithAPI && (
            <AuthenticatedNavContent
              handleContentOnTouchStart={handleContentOnTouchStart}
              handleContentOnTouchEnd={handleContentOnTouchEnd}
            />
          )}

          <FooterContainer auth={isAuthenticatedWithAPI}>
            {!isAuthenticatedWithAPI && (
              <ConnectWalletContainer>
                <ConnectWallet variant="mobile" loading={isConnectingAPIUser} />
              </ConnectWalletContainer>
            )}
            <FooterIcons />
          </FooterContainer>
        </NavContent>
      </SheetContainer>
    </Sheet>
  );
};

const SheetContainer = styled(Sheet.Content, {
  //need !important to override react sheet styles
  borderRadius: '16px 16px 0 0 !important',
});

const NavContent = styled('div', {
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  justifyContent: 'space-between',
  marginTop: 24,
  gap: 32,
  position: 'relative',

  height: '324px',

  variants: {
    auth: {
      true: {
        height: '100%',
        //max height 100vh is needed for mobile scroll
      },
    },
  },
});

const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 24,

  variants: {
    auth: {
      true: {
        gap: 16,
      },
    },
  },
});

const NavList = styled('div', {
  display: 'flex',
  listStyle: 'none',
  height: '100%',
  margin: '16px 16px 0 ',
  overflow: 'auto',

  ...StyledScroll,

  variants: {
    variant: {
      authenticated: {
        minHeight: 400,
      },
    },
  },
});

const ConnectWalletContainer = styled('div', {
  marginX: 16,
  marginBottom: 24,
});

const FooterContainer = styled('div', {
  justifyContent: 'center',
  marginTop: 'auto',
  marginBottom: 24,

  variants: {
    auth: {
      true: {
        marginBottom: 36,
      },
    },
  },
});

const StickyContainer = styled('div', {
  position: 'sticky',
  top: 0,
  left: 0,
  right: 0,
  background: '$white',
  zIndex: '$above1',
  borderTopRightRadius: 16,
  borderTopLeftRadius: 16,
  width: '100%',
});
const UserInfoContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 16px 0 16px',
  width: '100%',
});

const OptionsDivider = styled(Divider, {
  marginBottom: 0,
  marginTop: 16,
});
